import styled from 'styled-components';
import { useQueryClient, useMutation } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import { useRef, useState } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { Modal, ModalTitle, ModalBody, ModalFooter, MODAL_INNER_GAP } from 'design-system/src/components/Modal';
import Button, {
  baseButtonStyles,
  largeButtonStyles,
  borderButtonStyles,
  tertiaryButtonStyles,
  disabledButtonStyles,
} from 'design-system/src/components/Button/Button';
import { Tooltip } from 'design-system/src/components/Tooltip/Tooltip';
import HelpCircleFilled from 'design-system/src/assets/icons/helpCircleFilled.svg?react';
import CheckCircle from 'design-system/src/assets/icons/checkCircle.svg?react';
import Close from 'design-system/src/assets/icons/close.svg?react';
import Warning from 'design-system/src/assets/icons/warning.svg?react';
import { toast } from 'react-hot-toast';
import { Toast, StandardErrorToast } from 'design-system/src/components/Toast/Toast';
import { ErrorMessage } from 'design-system/src/components/ErrorMessage/ErrorMessage';
import { Typography } from 'design-system/src/components/Typography/Typography.tsx';
import { standardTransitionStyles, focusVisibleStyles } from 'design-system/src/sharedStyles';

import type { OrgMember, OrgMemberRole } from '../../../api';
import { client } from '../../../api';
import { DropdownWithPlaceholder, DropdownItem } from '../../Dropdown.tsx';
import { FormLabel } from '../../FormLabel.tsx';
import { getRoleOptions } from '../../signup/utils.tsx';
import { Input } from '../../Input.tsx';
import { EMAIL_VALIDATION_REGEX, DUPLICATE_EMAIL_ERROR } from '../../../constants.ts';
import { requiredFieldRule, enterPlaceholder, selectPlaceholder } from '../../../utils.tsx';
import { formatNameReversed } from 'shared-utils/utils/formatString';

const UploadFileLabel = styled.label<{ $disabled?: boolean }>`
  // TODO(design-system): add a method to Button.tsx that generates these styles
  ${baseButtonStyles}
  ${tertiaryButtonStyles}
  ${largeButtonStyles}
  ${standardTransitionStyles}
  ${focusVisibleStyles}
  ${borderButtonStyles}
  background-color: var(--white);
  border-color: var(--grayscale-400);
  width: fit-content;

  &:hover,
  &:focus-visible {
    background-color: var(--grayscale-50);
    border-color: var(--grayscale-400);
  }

  ${({ $disabled }) => $disabled && disabledButtonStyles}
`;

const InstructionsList = styled.ul`
  padding-inline-start: 24px;
  margin: 0;
`;

const HiddenInput = styled.input`
  display: none;
`;

const StyledModalBody = styled(ModalBody)`
  padding-bottom: ${MODAL_INNER_GAP};
  max-height: calc(100vh - 200px);
`;

const dropdownSx = { '& fieldset': { borderColor: 'var(--grayscale-400) !important' } };
const MAX_CSV_ROWS = 50;

interface AddMemberFormData {
  first_name: string;
  last_name: string;
  email: string;
  permissions: OrgMemberRole;
  role: string;
}

export type AddMembersModalProps = {
  onClose: () => void;
  members: OrgMember[];
  /** When true, shows the inline "Add user" button and the added users list. Defaults to false. */
  showAddedUsers?: boolean;
};

/**
 * A modal for adding one or more members of an organization.
 */
const AddMembersModal = ({ onClose, members, showAddedUsers = false }: AddMembersModalProps) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const isTrainingMode = import.meta.env.VITE_TRAINING_MODE === 'true';
  const [csvUploadCount, setCsvUploadCount] = useState<number | null>(null);
  const [csvUploadError, setCsvUploadError] = useState(false);

  const queryClient = useQueryClient();

  const {
    control,
    formState: { errors },
    handleSubmit,
    reset,
    setError,
    watch,
  } = useForm<AddMemberFormData>({
    defaultValues: {
      first_name: '',
      last_name: '',
      email: '',
      permissions: '' as AddMemberFormData['permissions'],
      role: '',
    },
  });

  const watchedValues = watch();
  const allFieldsFilled =
    watchedValues.first_name.trim() &&
    watchedValues.last_name.trim() &&
    watchedValues.email.trim() &&
    watchedValues.permissions &&
    watchedValues.role;

  const addMember = useMutation<{ success: boolean }, AxiosError<{ error: string }>, AddMemberFormData>({
    mutationFn: async (data) => {
      if (isTrainingMode) {
        console.warn('Training Mode: Mocking user creation, no real request sent.');
        return Promise.resolve({ success: true });
      }

      return client
        .post('org-members/add', [
          {
            first_name: data.first_name,
            last_name: data.last_name,
            email_address: data.email,
            job_title: data.role,
            role: data.permissions,
          },
        ])
        .then((res) => res.data);
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['orgMembers'] });
      toast.custom(() => <Toast variant="success">User added</Toast>);
      reset();
      if (!showAddedUsers) {
        onClose();
      }
    },
    onError: async (error) => {
      await queryClient.invalidateQueries({ queryKey: ['orgMembers'] });
      toast.custom(() => <StandardErrorToast />);
      console.error(error);
    },
  });

  const deleteMember = useMutation<{ success: boolean }, AxiosError<{ error: string }>, string>({
    mutationFn: async (memberId) => {
      if (isTrainingMode) {
        console.warn('Training Mode: Mocking user deletion, no real request sent.');
        return Promise.resolve({ success: true });
      }
      return client.post('org-members/delete', { member_id: memberId }).then((res) => res.data);
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['orgMembers'] });
      toast.custom(() => <Toast variant="success">User removed</Toast>);
    },
    onError: async (error) => {
      toast.custom(() => <StandardErrorToast />);
      console.error(error);
    },
  });

  const handleFileUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files && event.target.files.length > 0 ? event.target.files[0] : null;
    if (file) {
      const reader = new FileReader();
      reader.onload = async function (event) {
        const contents = event.target?.result;
        if (contents) {
          const lines = contents
            ?.toString()
            .split(/\r?\n/)
            .filter((line) => line.trim().length > 0)
            .map((line) => line.split(','));

          if (lines.length > MAX_CSV_ROWS) {
            toast.custom(() => (
              <Toast variant="error">CSV exceeds {MAX_CSV_ROWS} rows. Please upload a smaller file.</Toast>
            ));
            if (inputRef.current) {
              inputRef.current.value = '';
            }
            return;
          }

          const data = lines.map((row) => ({
            first_name: row[0]?.trim() || '',
            last_name: row[1]?.trim() || '',
            email_address: row[2]?.trim() || '',
            job_title: row[3]?.trim() || '',
            role: (row[4]?.trim()?.toLowerCase() === 'admin' ? 'stytch_admin' : 'stytch_member') as string,
          }));

          if (isTrainingMode) {
            console.warn('Training Mode: Mocking CSV upload, no real request sent.');
          } else {
            try {
              await client.post('org-members/add', data);
              await queryClient.invalidateQueries({ queryKey: ['orgMembers'] });
              setCsvUploadError(false);
              setCsvUploadCount(data.length);
            } catch (error) {
              await queryClient.invalidateQueries({ queryKey: ['orgMembers'] });
              setCsvUploadCount(null);
              setCsvUploadError(true);
              console.error(error);
            }
          }
        }

        if (inputRef.current) {
          inputRef.current.value = '';
        }
      };

      reader.readAsText(file);
    } else {
      console.error('Failed to load file');
    }
  };

  const onSubmit = (data: AddMemberFormData) => {
    const emailAlreadyExists = members.some(
      (member) => member.email_address.toLowerCase() === data.email.trim().toLowerCase(),
    );
    if (emailAlreadyExists) {
      setError('email', { message: DUPLICATE_EMAIL_ERROR });
      return;
    }
    addMember.mutate(data);
  };

  return (
    <Modal onClose={onClose}>
      <ModalTitle>Add users</ModalTitle>
      <StyledModalBody>
        <div className="flex flex-col items-start gap-2 self-stretch rounded-medium bg-background-gray p-4">
          <Typography styledAs="bodySpaceGrotesk" renderedAs="div" weight={500}>
            Add your staff using one of the following methods:
          </Typography>
          <Typography styledAs="bodySpaceGrotesk" renderedAs="div">
            <InstructionsList>
              <li>Add staff individually: Type the relevant information into each field below.</li>
              <li>CSV Upload: Upload a .csv file using the format below.</li>
            </InstructionsList>
          </Typography>

          <UploadFileLabel htmlFor="fileinput" tabIndex={0}>
            <HiddenInput type="file" id="fileinput" ref={inputRef} onChange={handleFileUpload} accept=".csv" />
            <Typography renderedAs="span" styledAs="bodySmallSpaceGrotesk" weight="500">
              Upload .csv file
            </Typography>
          </UploadFileLabel>
          {csvUploadCount !== null && (
            <div className="flex items-center gap-1">
              <CheckCircle className="size-4 text-green-500" aria-hidden />
              <Typography styledAs="bodyDMSans" color="var(--green-500)">
                {csvUploadCount} users added successfully!
              </Typography>
            </div>
          )}
          {csvUploadError && (
            <div className="flex items-center gap-1">
              <Warning className="size-4 text-yellow-600" />
              <Typography styledAs="bodyDMSans" color="var(--yellow-600)">
                Error loading .csv. Please try again
              </Typography>
            </div>
          )}
        </div>

        <form
          onSubmit={handleSubmit(onSubmit)}
          className="flex flex-col gap-3 rounded-medium border border-solid border-grayscale-100 p-3 [&_fieldset]:!border-grayscale-400 [&_input:not([type='file'])]:!border-grayscale-400"
        >
          <div className="flex gap-4">
            <div className="flex-1">
              <Controller
                control={control}
                name="first_name"
                rules={{ required: requiredFieldRule('First name') }}
                render={({ field }) => (
                  <Input
                    label="First name"
                    placeholder={enterPlaceholder('first name')}
                    hasError={!!errors.first_name}
                    aria-describedby="first-name-error"
                    {...field}
                  />
                )}
              />
              {errors.first_name && <ErrorMessage id="first-name-error">{errors.first_name.message}</ErrorMessage>}
            </div>
            <div className="flex-1">
              <Controller
                control={control}
                name="last_name"
                rules={{ required: requiredFieldRule('Last name') }}
                render={({ field }) => (
                  <Input
                    label="Last name"
                    placeholder={enterPlaceholder('last name')}
                    hasError={!!errors.last_name}
                    aria-describedby="last-name-error"
                    {...field}
                  />
                )}
              />
              {errors.last_name && <ErrorMessage id="last-name-error">{errors.last_name.message}</ErrorMessage>}
            </div>
          </div>

          <div className="flex gap-4">
            <div className="flex flex-1 flex-col">
              <Controller
                control={control}
                name="permissions"
                rules={{ required: requiredFieldRule('Permissions') }}
                render={({ field }) => (
                  <>
                    <FormLabel id="permissions-label">
                      <span className="inline-flex items-center gap-1">
                        Permissions
                        <Tooltip
                          maxWidth="300px"
                          childNotInteractive
                          content="Admins are granted permissions to manage users, and other team settings like NPI and office affiliations."
                        >
                          <HelpCircleFilled
                            title="help"
                            className="inline-block size-4 cursor-help align-middle text-grayscale-400"
                          />
                        </Tooltip>
                      </span>
                    </FormLabel>
                    <DropdownWithPlaceholder
                      placeholder={selectPlaceholder('permissions')}
                      $hasError={!!errors.permissions}
                      aria-labelledby="permissions-label"
                      aria-describedby="permissions-error"
                      sx={dropdownSx}
                      {...field}
                    >
                      <DropdownItem value="stytch_member">Non-admin</DropdownItem>
                      <DropdownItem value="stytch_admin">Admin</DropdownItem>
                    </DropdownWithPlaceholder>
                  </>
                )}
              />
              {errors.permissions && <ErrorMessage id="permissions-error">{errors.permissions.message}</ErrorMessage>}
            </div>
            <div className="flex flex-1 flex-col">
              <Controller
                control={control}
                name="role"
                rules={{ required: requiredFieldRule('Role') }}
                render={({ field }) => (
                  <>
                    <FormLabel id="role-label">Role</FormLabel>
                    <DropdownWithPlaceholder
                      placeholder={selectPlaceholder('role')}
                      $hasError={!!errors.role}
                      aria-labelledby="role-label"
                      aria-describedby="role-error"
                      sx={dropdownSx}
                      {...field}
                    >
                      {getRoleOptions()}
                    </DropdownWithPlaceholder>
                  </>
                )}
              />
              {errors.role && <ErrorMessage id="role-error">{errors.role.message}</ErrorMessage>}
            </div>
          </div>

          <Controller
            control={control}
            name="email"
            rules={{
              required: requiredFieldRule('Email'),
              pattern: {
                value: EMAIL_VALIDATION_REGEX,
                message: 'Please enter a valid email address',
              },
            }}
            render={({ field }) => (
              <Input
                type="email"
                label="Email"
                placeholder={enterPlaceholder('email address')}
                hasError={!!errors.email}
                aria-describedby="email-error"
                {...field}
              />
            )}
          />
          {errors.email && <ErrorMessage id="email-error">{errors.email.message}</ErrorMessage>}

          {showAddedUsers && (
            <Button type="submit" size="sm" isLoading={addMember.isPending} disabled={!allFieldsFilled}>
              Add user
            </Button>
          )}
        </form>

        {showAddedUsers && members.length > 0 && (
          <ul
            aria-label="Added users"
            className="-mt-2 flex max-h-[200px] list-none flex-col gap-2 self-stretch overflow-y-auto p-0"
          >
            {members.map((member) => (
              <li
                key={member.id}
                className={`flex items-center justify-between rounded-medium bg-background-gray px-2 py-1.5 transition-opacity ${deleteMember.isPending && deleteMember.variables === member.id ? 'opacity-50' : ''}`}
              >
                <div className="flex items-center gap-2">
                  <Tooltip
                    content="Unable to remove yourself"
                    childNotInteractive={member.is_current_user}
                    disabled={!member.is_current_user}
                    placement="bottom"
                  >
                    <button
                      type="button"
                      onClick={() => deleteMember.mutate(member.id)}
                      disabled={member.is_current_user || deleteMember.isPending}
                      aria-label={`Remove ${member.name || member.email_address}`}
                      className="group flex shrink-0 cursor-pointer items-center border-0 bg-transparent p-1.5 disabled:cursor-not-allowed"
                    >
                      <Close className="size-5 text-grayscale-400 group-disabled:text-grayscale-200" />
                    </button>
                  </Tooltip>
                  <Typography styledAs="bodySmallDMSans" weight={500}>
                    {member.name
                      ? formatNameReversed(member.name.split(' ')[0], member.name.split(' ').slice(1).join(' '))
                      : member.email_address}
                  </Typography>
                  <Typography styledAs="bodySmallDMSans" color="var(--grayscale-500)">
                    {member.email_address}
                  </Typography>
                </div>
                {member.is_current_user && (
                  <Typography styledAs="bodySmallDMSans" color="var(--grayscale-500)">
                    You
                  </Typography>
                )}
              </li>
            ))}
          </ul>
        )}

        <ErrorMessage visible={addMember.isError}>{addMember.error?.response?.data.error}</ErrorMessage>
      </StyledModalBody>
      {!showAddedUsers && (
        <ModalFooter>
          <Button variant="tertiary" onClick={onClose}>
            Back
          </Button>
          <Button
            type="button"
            onClick={handleSubmit(onSubmit)}
            isLoading={addMember.isPending}
            disabled={!allFieldsFilled}
          >
            Submit
          </Button>
        </ModalFooter>
      )}
    </Modal>
  );
};

export default AddMembersModal;
